import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  CARD_TRIAL_DAYS,
  formatRenewalPrice,
  formatShortDate,
  termCount,
  type ProTrialTerms,
} from '@/lib/card-trial';
import {
  iconArrowRight,
  iconCheck,
  iconBolt,
  iconCircleInfo,
  iconCreditCard,
  iconRocket,
  iconSpinnerThird,
  iconStarSolid,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { TrialRenewal } from '@/types';
import { BRAND, brandAsset } from '@/lib/brand';

const DAY_MS = 24 * 60 * 60 * 1000;

type Cell = string | boolean;

const upTo = (seats: number | null) => (seats == null ? '—' : `Up to ${seats}`);

// Pro trial vs Starter vs Free, the same rows as cleanlist.ai/pricing. The
// numbers come from the API (ProTrialTerms), never a copy kept here.
const comparison = (
  terms: ProTrialTerms
): { feature: string; pro: Cell; starter: Cell; free: Cell }[] => [
  {
    feature: 'Searches',
    pro: termCount(terms.trialSearches),
    starter: termCount(terms.starterSearches, '/mo'),
    free: termCount(terms.freeSearches),
  },
  {
    feature: 'Seats',
    pro: upTo(terms.trialSeats),
    starter: 'Unlimited',
    free: upTo(terms.freeSeats),
  },
  {
    feature: 'Waterfall enrichment',
    pro: true,
    starter: true,
    free: true,
  },
  { feature: 'Clu', pro: true, starter: true, free: true },
  {
    feature: 'Email and call sequences',
    pro: true,
    starter: true,
    free: false,
  },
  { feature: 'LinkedIn steps', pro: true, starter: false, free: false },
  { feature: 'AI agents', pro: true, starter: false, free: false },
  // HubSpot and Salesforce are the CRMs the API syncs with (Pipedrive is
  // refused), and CRM sync starts on Pro.
  {
    feature: 'HubSpot and Salesforce sync',
    pro: true,
    starter: false,
    free: false,
  },
  { feature: 'Clu in Slack', pro: true, starter: false, free: false },
];

// Where the 4.8 average comes from (G2 5.0 ×5, Chrome 4.7 ×13, AppSumo 4.8 ×112).
const REVIEW_SOURCES = [
  { src: '/logos/g2.png', name: 'G2' },
  { src: '/logos/chrome-web-store-mark.png', name: 'Chrome Web Store' },
  { src: '/logos/appsumo-mark.png', name: 'AppSumo' },
];

interface TimelineStep {
  icon: IconDefinition;
  when: string;
  title: string;
  body: string;
}

/** The trial offer's "today → renewal" steps; the first one is highlighted. */
function TrialTimeline({ steps }: { steps: TimelineStep[] }) {
  return (
    <ol className='mt-9'>
      {steps.map((step, i) => (
        <li key={step.when} className='relative flex gap-4 pb-6 last:pb-0'>
          {i < steps.length - 1 && (
            <span
              aria-hidden='true'
              className={cn(
                'absolute top-10 bottom-1 left-[17px] w-0.5 rounded-full',
                i === 0
                  ? 'bg-linear-to-b from-primary to-primary/30'
                  : 'bg-border'
              )}
            />
          )}
          <span
            className={cn(
              'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
              i === 0
                ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30'
                : 'bg-muted text-muted-foreground ring-1 ring-border'
            )}
          >
            <AppIcon icon={step.icon} className='h-3.5 w-3.5' />
          </span>
          <div className='pt-0.5'>
            <p
              className={cn(
                'text-xs font-medium',
                i === 0 ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              {step.when}
            </p>
            <p className='mt-0.5 text-[15px] font-semibold text-foreground'>
              {step.title}
            </p>
            <p className='mt-0.5 text-sm leading-6 text-muted-foreground'>
              {step.body}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function ReviewProof({ audience }: { audience: string }) {
  return (
    <div className='mt-8 flex items-center gap-3 border-t border-border pt-7'>
      <div className='flex -space-x-2'>
        {REVIEW_SOURCES.map((source) => (
          <span
            key={source.name}
            className='flex h-9 w-9 items-center justify-center rounded-full bg-background ring-2 ring-background'
            title={source.name}
          >
            <img
              src={source.src}
              alt={source.name}
              className='h-5 w-5 object-contain'
            />
          </span>
        ))}
      </div>
      <div>
        <p className='flex items-center gap-1.5 text-sm font-semibold text-foreground'>
          <span className='flex gap-0.5 text-[#ffb020]' aria-hidden='true'>
            {Array.from({ length: 5 }, (_, i) => (
              <AppIcon key={i} icon={iconStarSolid} className='h-3 w-3' />
            ))}
          </span>
          4.8 out of 5
        </p>
        <p className='text-[13px] text-muted-foreground'>
          From 130 reviews · {audience}
        </p>
      </div>
    </div>
  );
}

/** A comparison-table cell: a check, or a dash for "not included". */
function Tick({ on, highlight }: { on: boolean; highlight?: boolean }) {
  if (!on) {
    return (
      <span className='text-white/25'>
        <span aria-hidden='true'>—</span>
        <span className='sr-only'>Not included</span>
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex h-5 w-5 items-center justify-center rounded-full',
        highlight ? 'bg-[#316bff] text-white' : 'bg-white/10 text-white/70'
      )}
    >
      <AppIcon icon={iconCheck} className='h-2.5 w-2.5' />
      <span className='sr-only'>Included</span>
    </span>
  );
}

const seatLabel = (seats: number) => `${seats} seat${seats === 1 ? '' : 's'}`;

/**
 * Pricing v4 card-required trial: 14 days of Pro, then Pro at the renewal
 * price from the entitlements payload. Presentational; TrialActivateGate owns
 * checkout, analytics and the skip-to-Free confirmation.
 */
export function ProTrialOffer({
  renewal,
  terms,
  busy,
  cancelled = false,
  inDialog = false,
  headerActions,
  continueLabel = 'Continue on Free',
  onStart,
  onContinueFree,
}: {
  renewal: TrialRenewal | null | undefined;
  /** The trial's and the plans' numbers, from the API. */
  terms: ProTrialTerms;
  busy: boolean;
  /** Back from a Stripe Checkout the user closed (`?trial=cancelled`). */
  cancelled?: boolean;
  /** Opened from the offer banner, over the app's own <main>. */
  inDialog?: boolean;
  /** Org switcher and sign out, or the dialog's close button. */
  headerActions?: ReactNode;
  continueLabel?: string;
  onStart: () => void;
  onContinueFree: () => void;
}) {
  const endsAt = Date.now() + CARD_TRIAL_DAYS * DAY_MS;
  const renewalDay = formatShortDate(endsAt);
  const price = renewal ? formatRenewalPrice(renewal) : null;
  const seats = renewal ? seatLabel(renewal.seats) : null;

  const timeline: TimelineStep[] = [
    {
      icon: iconBolt,
      when: 'Today',
      title: 'Every Pro feature, free',
      body: `${[
        `${termCount(terms.trialCredits)} credits`,
        terms.trialSearches == null
          ? null
          : `${termCount(terms.trialSearches)} searches`,
      ]
        .filter(Boolean)
        .join(' and ')}${
        terms.trialSeats == null
          ? ''
          : `, shared by up to ${terms.trialSeats} teammates`
      }. Build agents, LinkedIn sequences and CRM sync.`,
    },
    {
      icon: iconRocket,
      when: 'Whenever you are ready',
      title: 'Launch on a paid plan',
      body: 'Agents and sequences go live once you are on Starter or Pro. Start early from Plans at any time.',
    },
    {
      icon: iconCreditCard,
      when: renewalDay,
      title: 'Your trial becomes Pro',
      body: price
        ? `Billed at ${price} for ${seats}, before tax. Prefer Starter, or want to cancel? Two clicks in Plans.`
        : 'Loading your renewal price. Checkout opens once it is ready.',
    },
  ];

  const Root = inDialog ? 'div' : 'main';
  const cell =
    'flex items-center justify-center border-t border-white/[0.07] px-1 py-2 text-center';

  return (
    <Root
      className='flex min-h-dvh flex-col bg-background lg:flex-row'
      aria-labelledby='trial-activate-title'
      aria-busy={busy}
    >
      <section className='flex flex-1 flex-col px-6 py-6 sm:px-10 lg:px-16'>
        <header className='flex items-center justify-between gap-3'>
          <img
            src={brandAsset('/logo-dark.png')}
            alt={BRAND.name}
            className='h-8 w-auto dark:hidden'
          />
          <img
            src={brandAsset('/logo-light.png')}
            alt={BRAND.name}
            className='hidden h-8 w-auto dark:block'
          />
          {headerActions}
        </header>

        <div className='flex flex-1 items-center justify-center py-10'>
          <div className='w-full max-w-[460px]'>
            <h1
              id='trial-activate-title'
              className='text-[38px] leading-[1.08] font-semibold tracking-[-0.035em] text-foreground sm:text-[44px]'
            >
              Try {BRAND.name} Pro free for {CARD_TRIAL_DAYS} days
            </h1>
            <p className='mt-4 text-[17px] leading-7 text-muted-foreground'>
              Nothing is charged today. Switch plans or cancel anytime from
              inside the app.
            </p>

            <TrialTimeline steps={timeline} />

            {cancelled && (
              <p
                role='status'
                className='mt-8 flex items-center gap-2 text-[13px] text-muted-foreground'
              >
                <AppIcon icon={iconCircleInfo} className='h-3.5 w-3.5' />
                Checkout was closed. Nothing was charged.
              </p>
            )}

            <Button
              variant='brand'
              className={cn(
                'h-[52px] w-full rounded-xl text-base font-semibold shadow-lg shadow-primary/25',
                cancelled ? 'mt-4' : 'mt-9'
              )}
              onClick={onStart}
              disabled={busy || !renewal}
            >
              {busy ? <AppIcon icon={iconSpinnerThird} spin /> : null}
              Start my Pro trial
              {!busy && (
                <AppIcon icon={iconArrowRight} className='h-3.5 w-3.5' />
              )}
            </Button>
            <p className='mt-3 text-center text-[13px] text-muted-foreground'>
              $0 due today · Secured by Stripe · Cancel in two clicks
            </p>

            <ReviewProof audience='Used by 10,000+ people' />
          </div>
        </div>

        <div className='pt-2 text-center'>
          <button
            type='button'
            onClick={onContinueFree}
            disabled={busy}
            className='text-[13px] text-muted-foreground/70 underline-offset-4 hover:text-muted-foreground hover:underline disabled:opacity-50'
          >
            {continueLabel}
          </button>
        </div>
      </section>

      <aside className='relative overflow-hidden bg-[#0b0d12] px-6 py-12 text-white sm:px-10 lg:flex lg:w-[46%] lg:items-center lg:px-14'>
        <div
          aria-hidden='true'
          className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_70%_0%,rgba(49,107,255,0.28),transparent_70%)]'
        />
        <div className='relative mx-auto w-full max-w-[520px]'>
          <h2 className='text-2xl font-semibold tracking-[-0.02em]'>
            Your Pro trial vs. Starter and Free
          </h2>
          <p className='mt-2 text-sm leading-6 text-white/60'>
            You get all of Pro for {CARD_TRIAL_DAYS} days. Here&apos;s what each
            plan includes after that.
          </p>

          <div className='mt-7 overflow-hidden rounded-2xl bg-white/[0.03] ring-1 ring-white/10'>
            <div className='grid grid-cols-[1fr_92px_92px_72px] text-sm'>
              <div />
              <div className='bg-[#316bff]/15 px-2 pt-4 pb-3 text-center'>
                <p className='font-semibold'>Pro trial</p>
                <p className='mt-0.5 text-xs font-medium text-[#8fb6ff]'>
                  Free · {CARD_TRIAL_DAYS} days
                </p>
              </div>
              <div className='px-2 pt-4 pb-3 text-center'>
                <p className='font-semibold text-white/80'>Starter</p>
                <p className='mt-0.5 text-xs text-white/50'>
                  {terms.starterPrice == null
                    ? '—'
                    : `$${terms.starterPrice.toLocaleString('en-US')}`}
                  /seat/mo
                </p>
              </div>
              <div className='px-2 pt-4 pb-3 text-center'>
                <p className='font-semibold text-white/80'>Free</p>
                <p className='mt-0.5 text-xs text-white/50'>$0</p>
              </div>

              <div className='flex items-center border-t border-white/[0.07] px-4 py-2 text-white/75'>
                Credits
              </div>
              <div className={cn(cell, 'bg-[#316bff]/15 font-semibold')}>
                {termCount(terms.trialCredits)}
              </div>
              <div className={cn(cell, 'text-xs text-white/70')}>
                {termCount(terms.starterCreditsPerSeat, '/seat/mo')}
              </div>
              <div className={cn(cell, 'text-xs text-white/60')}>
                {termCount(terms.freeCredits, '/mo')}
              </div>

              {comparison(terms).map((row) => (
                <div key={row.feature} className='contents'>
                  <div className='flex items-center border-t border-white/[0.07] px-4 py-2 text-white/75'>
                    {row.feature}
                  </div>
                  <div
                    className={cn(cell, 'bg-[#316bff]/15 text-xs font-medium')}
                  >
                    {typeof row.pro === 'string' ? (
                      row.pro
                    ) : (
                      <Tick on={row.pro} highlight />
                    )}
                  </div>
                  <div className={cn(cell, 'text-xs text-white/70')}>
                    {typeof row.starter === 'string' ? (
                      row.starter
                    ) : (
                      <Tick on={row.starter} />
                    )}
                  </div>
                  <div className={cn(cell, 'text-xs text-white/60')}>
                    {typeof row.free === 'string' ? (
                      row.free
                    ) : (
                      <Tick on={row.free} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className='mt-6 text-[13px] leading-6 text-white/55'>
            Your workspace stays on Pro after {renewalDay}. Prefer Starter?
            Switch in Plans any time before then. Your lists, enrichments and
            credits carry over.
          </p>
          <p className='mt-3 text-xs leading-5 text-white/40'>
            During the trial you can build agents and sequences. They launch on
            a paid plan.
          </p>
        </div>
      </aside>
    </Root>
  );
}
